import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { appThemeModeLabels, type AppThemeMode } from "../theme/appTheme";
import { useAppTheme } from "../theme/ThemeProvider";
import {
  activeViewFromPathname,
  allowedViewsForUser,
  persistActiveProductView,
  routeForView,
  viewLabel,
  type FswlView,
} from "../auth/viewAccess";
import { useAuth } from "../state/AuthProvider";

export type AccountMenuModel = {
  userName: string;
  userInitials: string;
  isSystemOwner: boolean;
  activeView: Exclude<FswlView, "customer">;
  activeViewLabel: string;
  switchableViews: Exclude<FswlView, "customer">[];
  showSwitchView: boolean;
  themeMode: AppThemeMode;
  setThemeMode(mode: AppThemeMode): void;
  themeOptions: { mode: AppThemeMode; label: string }[];
  navigateTo(path: string): void;
  switchToView(view: Exclude<FswlView, "customer">): void;
  signOut(): Promise<void>;
};

export function useAccountMenuModel(): AccountMenuModel | null {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, setMode } = useAppTheme();

  const switchableViews = useMemo(
    () => (user ? allowedViewsForUser(user) : []),
    [user],
  );

  const activeView = activeViewFromPathname(location.pathname);

  const signOut = useCallback(async () => {
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  const switchToView = useCallback(
    (view: Exclude<FswlView, "customer">) => {
      if (!user || !switchableViews.includes(view)) return;
      persistActiveProductView(view);
      navigate(routeForView(view));
    },
    [navigate, switchableViews, user],
  );

  if (!user) return null;

  const initials = user.name
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return {
    userName: user.name,
    userInitials: initials || "??",
    isSystemOwner: user.isAdmin === true,
    activeView,
    activeViewLabel: viewLabel(activeView),
    switchableViews,
    showSwitchView: switchableViews.length > 1,
    themeMode: mode,
    setThemeMode: setMode,
    themeOptions: (["light", "dark", "system"] as AppThemeMode[]).map((themeMode) => ({
      mode: themeMode,
      label: appThemeModeLabels[themeMode],
    })),
    navigateTo: (path) => navigate(path),
    switchToView,
    signOut,
  };
}
